import styled from '@emotion/styled'
import { Drawer } from '@mui/material'
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import styles from './RightDrawerSelector.module.scss'
import { MUIButton } from '../../src/Components/Shared'
import CloseIcon from '@mui/icons-material/Close'
import ServerIcon from '../Components/Icons/ServerIcon'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SearchIcon from '@mui/icons-material/Search'
import {
  RightDrawerSubmit,
  IKeyValue,
  FetchRightDrawerLayout,
} from '../Models/CommonListHeadingModal'
import { renderFilterList } from '../Components/Molecule/CategoryFormData/CommonHelperFunctions'
import useDebounce from '../Hooks/useDebounce'
import { useFetchOnOpen } from '../Hooks/useFetchOnOpen'

const CustomCSSDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: '1201 !important',
}))

type LayoutItem = { key: string; value: string; moduleType: string }

interface RightDrawerSelectorProps {
  open: boolean
  onCancel: (open: boolean) => void
  drawerTitle: string
  fetchTypes: () => Promise<IKeyValue[]>
  fetchLayouts: ({ moduleKey, layoutKey }: FetchRightDrawerLayout) => Promise<LayoutItem[]>
  onSubmit: ({ selectedLayout, selectedType }: RightDrawerSubmit) => void
  emptyTypePlaceholder?: string
  emptyLayoutPlaceholder?: string
  moduleType?: 'Container' | 'Widget'
  defaultLayout?: string
}

type SearchLayoutField = {
  placeholder: string
  value: string
  setter: Dispatch<SetStateAction<string>>
  inputRef?: React.RefObject<HTMLInputElement>
  autoFocus?: boolean
}

const RightDrawerSelector: React.FC<RightDrawerSelectorProps> = ({
  open,
  onCancel,
  drawerTitle,
  fetchTypes,
  fetchLayouts,
  onSubmit,
  emptyLayoutPlaceholder = 'No layout found',
  moduleType = 'Widget',
  defaultLayout,
}) => {
  const [trayTypeList, setTrayTypeList] = useState<IKeyValue[]>([])
  const [assetLayoutsList, setAssetLayoutsList] = useState<LayoutItem[]>([])
  const [selectedTypeData, setSelectedTypeData] = useState('')
  const [selectedLayoutData, setSelectedLayoutData] = useState('')
  const [searchText, setSearchText] = useState('')
  const [footerAnim, setFooterAnim] = useState(false)
  const [typeLayoutSearchText, setTypeLayoutSearchText] = useState('')

  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const searchValue = useDebounce(typeLayoutSearchText, 1000)

  const computedEmptyTypePlaceholder = `Choose a ${moduleType.toLowerCase()} type to get started`
  const filteredLayouts = assetLayoutsList.filter((layout) =>
    layout.key.toLowerCase().includes(searchText.toLowerCase()),
  )

  useFetchOnOpen(open, () => {
    fetchTypes().then((list) => setTrayTypeList(list))
  })

  const typeLabel = `Select ${moduleType} Type`
  const buttonLabel = `Create ${moduleType}`

  useEffect(() => {
    if (trayTypeList.length === 1) {
      setSelectedTypeData(trayTypeList[0].value)
      if (searchValue.trim().length === 0) handleTypeSelection({ moduleKey: trayTypeList[0].value })
    }
  }, [trayTypeList])
  useEffect(() => {
    resetTypeAndLayoutSelection()
    if (searchValue.trim().length > 0) {
      handleTypeSelection({ layoutKey: searchValue })
    } else {
      fetchTypes().then((list) => setTrayTypeList(list))
    }
  }, [searchValue])

  const handleTypeSelection = async (val: FetchRightDrawerLayout) => {
    if (val.moduleKey === selectedTypeData) return
    resetTypeAndLayout()
    setSelectedTypeData(val?.moduleKey ?? '')

    try {
      const layouts = await fetchLayouts(val)
      setAssetLayoutsList(layouts)
      if (typeLayoutSearchText.trim().length > 0) {
        const uniqueTypeValues = [...new Set(layouts.map((curr) => curr.moduleType))]

        const filteredTypes = uniqueTypeValues.map((typeValue) => ({
          key: typeValue,
          value: typeValue,
        }))
        setTrayTypeList(filteredTypes)
        if (filteredTypes.length === 0) {
          setSelectedTypeData('')
          setSelectedLayoutData('')
        }
      }

      if (defaultLayout && layouts?.length === 1) {
        const match = layouts?.find((layout) => layout?.key === defaultLayout)
        setSelectedLayoutData(match ? match.value : layouts[0]?.value)
      }
    } catch (error) {
      setAssetLayoutsList([])
    }
  }

  const handleLayoutSelection = (val: LayoutItem) => {
    setSelectedLayoutData(val.value)
    if (typeLayoutSearchText.trim().length > 0 && val.moduleType !== selectedTypeData) {
      setSelectedTypeData(val.moduleType)
    }
  }

  useEffect(() => {
    if (open) {
      resetTypeAndLayoutSelection()
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open])

  useEffect(() => {
    setFooterAnim(selectedLayoutData.trim() !== '')
  }, [selectedLayoutData])

  const resetTypeAndLayoutSelection = () => {
    setSelectedTypeData('')
    resetTypeAndLayout()
  }
  const resetTypeAndLayout = () => {
    setSelectedLayoutData('')
    setSearchText('')
    setAssetLayoutsList([])
  }
  const displaySearchField = ({
    placeholder,
    setter,
    value,
    inputRef,
    autoFocus,
  }: SearchLayoutField) => {
    return (
      <div className={`${styles.layoutSearchWrapper}`}>
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type='text'
          placeholder={placeholder}
          value={value}
          onChange={(e) => setter(e.target.value)}
          className={styles.layoutSearchInput}
        />
        <SearchIcon className={styles.layoutSearchIcon} />
      </div>
    )
  }

  return (
    <CustomCSSDrawer
      anchor='right'
      open={open}
      variant='temporary'
      PaperProps={{
        sx: {
          width: '428px',
        },
      }}
      SlideProps={{
        direction: 'left',
      }}
      transitionDuration={600}
    >
      <div className={styles['drawer-wrapper']}>
        <div className={styles.drawerHeader}>
          <span
            data-testid='title'
            className={`heading-4 ${styles.sectionTitle} ${styles.drawerTitle}`}
          >
            {drawerTitle}
          </span>
          <button
            type='button'
            onClick={() => {
              onCancel(false)
              setTypeLayoutSearchText('')
            }}
            aria-label='Close'
            className={styles.closeButton}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.stepperHeader}>
          <div className={styles.stepperTopRow}>
            <span className={styles.stepperStep}>
              {selectedTypeData === '' ? 'Step 1 to 2' : 'Step 2 to 2'}
            </span>
            <span className={styles.stepperType}>{'Select Type'}</span>
          </div>
          <div className={styles.stepperBarWrapper}>
            <div
              className={styles.stepperBar}
              style={{
                width: selectedTypeData === '' ? '0%' : selectedLayoutData === '' ? '50%' : '100%',
              }}
            />
          </div>
          {displaySearchField({
            placeholder: `Search custom ${moduleType.toLowerCase()} layouts...`,
            setter: setTypeLayoutSearchText,
            value: typeLayoutSearchText,
            inputRef: searchInputRef,
            autoFocus: true,
          })}
        </div>
        <div className={styles['horizontal-line']} />
        <div className={`heading-6 ${styles.sectionWrapper}`}>
          <p className={styles.typeLabel}>{typeLabel}</p>
          <div className={styles.typeListWrapper}>
            {renderFilterList({
              filterList: trayTypeList,
              onClick: (val: string) =>
                handleTypeSelection({
                  moduleKey: val,
                  ...(typeLayoutSearchText ? { layoutKey: typeLayoutSearchText } : {}),
                }),
              selectedValue: selectedTypeData,
              shouldHover: true,
              keyType: 'value',
            })}
          </div>
          <div className={styles['horizontal-line']} />
          {selectedTypeData === '' && assetLayoutsList.length === 0 && (
            <div className={styles.emptyTypePlaceholder} data-testid='empty-placeholder'>
              <div className={styles.emptyTypeIcon}>
                <ServerIcon />
              </div>
              <div className={styles.emptyTypeTitle}>{computedEmptyTypePlaceholder}</div>
              <div className={styles.emptyTypeDesc}>
                Select from the options above to see available layouts
              </div>
            </div>
          )}
          {assetLayoutsList.length > 0 && (
            <div className={`${styles.layoutSectionWrapper} ${styles['idle']}`}>
              <p className={styles.layoutSectionTitle}>
                Select Layout
                <span className={styles.layoutCount}>{filteredLayouts.length} layouts</span>
              </p>
              {displaySearchField({
                placeholder: `Search ${selectedTypeData.toLowerCase()} layouts...`,
                setter: setSearchText,
                value: searchText,
                inputRef: searchInputRef,
                autoFocus: true,
              })}
              <div className={styles.layoutListWrapper}>
                <ul className={styles.layoutGridList}>
                  {filteredLayouts.map((element) => {
                    const isActive = element.value === selectedLayoutData
                    return (
                      <li
                        data-testid={`${element.key.replace(/\s/g, '')}Layout`}
                        key={`${element.moduleType}_${element.key}`}
                        aria-hidden='true'
                        className={`${styles.layoutGridItem} ${isActive ? styles.active : ''}`}
                        onClick={() => handleLayoutSelection(element)}
                      >
                        <span className={styles.layoutGridItemText}>{element.key}</span>
                        {isActive && (
                          <span className={styles.tickIcon}>
                            <CheckCircleIcon fontSize='small' />
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
                {filteredLayouts.length === 0 && searchText && (
                  <div className={styles.emptyLayoutPlaceholder} data-testid='empty-placeholder'>
                    <div className={styles.emptyLayoutIcon}>
                      <ServerIcon />
                    </div>
                    <div className={styles.emptyLayoutTitle}>{emptyLayoutPlaceholder}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedLayoutData.trim() !== '' && (
            <div className={`${styles['drawer-footer']} ${footerAnim ? styles.footerAnimIn : ''}`}>
              <MUIButton
                width={176}
                height='48px'
                type='button'
                label={buttonLabel}
                variant='contained'
                handleClickCB={() => {
                  onSubmit({ selectedType: selectedTypeData, selectedLayout: selectedLayoutData })
                }}
                disabled={selectedLayoutData.trim() === ''}
                className={styles.footerButton}
              />
              {selectedTypeData && selectedLayoutData && (
                <div className={styles['footer-summary']}>
                  {selectedTypeData} &gt;{' '}
                  {assetLayoutsList.find((layout) => layout.value === selectedLayoutData)?.key ||
                    ''}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </CustomCSSDrawer>
  )
}

export default RightDrawerSelector
